import { useState } from "react";
import { ChevronDown, Pencil, X, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";
import { useLang, dictionaries } from "@/i18n";
import { SITE_PAGES } from "@/siteContentSchema";

const getPath = (obj, path) => path.split(".").reduce((o, k) => (o && o[k] != null ? o[k] : ""), obj);
const safeId = (key) => key.replace(/[^a-zA-Z0-9]/g, "-");

export default function SiteContentTab({ t }) {
  const { overrides, simg, reloadContent } = useLang();
  const [openPage, setOpenPage] = useState(null);
  const [editing, setEditing] = useState(null);
  const [fr, setFr] = useState("");
  const [en, setEn] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);

  const startEdit = (key) => {
    const ov = overrides.texts[key];
    setEditing(key);
    setFr(ov?.fr || getPath(dictionaries.fr, key));
    setEn(ov?.en || getPath(dictionaries.en, key));
  };

  const saveText = async (key) => {
    setSaving(true);
    try {
      await api.put("/admin/content/text", { key, fr, en });
      await reloadContent();
      setEditing(null);
      toast.success(t.admin.updated);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (key, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.put("/admin/content/image", { key, url: data.url });
      await reloadContent();
      toast.success(t.admin.updated);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(null);
      e.target.value = "";
    }
  };

  return (
    <section className="mt-10" data-testid="site-content-section">
      <p className="mb-6 text-sm text-maquis-soft">{t.admin.site_hint}</p>
      <div className="space-y-3">
        {SITE_PAGES.map((page) => {
          const open = openPage === page.id;
          return (
            <div key={page.id} data-testid={`site-page-${page.id}`} className="rounded-md border border-maquis-deep/10 bg-casse">
              <button
                data-testid={`site-page-toggle-${page.id}`}
                onClick={() => setOpenPage(open ? null : page.id)}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
              >
                <span className="font-serif text-xl text-maquis-deep">{page.label}</span>
                <ChevronDown size={18} strokeWidth={1.5} className={`text-maquis-olive transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
              </button>
              {open && (
                <div className="border-t border-maquis-deep/10 px-5 py-5">
                  <p className="field-label">{t.admin.site_texts}</p>
                  <ul className="mt-2 divide-y divide-maquis-deep/5">
                    {page.texts.map(({ key, label }) => {
                      const current = overrides.texts[key]?.fr || getPath(dictionaries.fr, key);
                      const isEditing = editing === key;
                      return (
                        <li key={key} data-testid={`site-text-${safeId(key)}`} className="py-3">
                          <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0">
                              <p className="text-xs uppercase tracking-[0.12em] text-maquis-soft">{label}</p>
                              {!isEditing && (
                                <p className="mt-1 line-clamp-2 text-sm text-maquis-deep">{current}</p>
                              )}
                            </div>
                            {!isEditing && (
                              <button
                                data-testid={`site-text-edit-${safeId(key)}`}
                                onClick={() => startEdit(key)}
                                aria-label={t.admin.site_edit}
                                className="mt-1 shrink-0 rounded-full border border-maquis-deep/15 p-2 text-maquis-deep transition-colors hover:bg-ivoire"
                              >
                                <Pencil size={13} strokeWidth={1.75} />
                              </button>
                            )}
                          </div>
                          {isEditing && (
                            <div className="mt-3 space-y-3">
                              <div>
                                <label className="field-label">FR</label>
                                <textarea data-testid={`site-text-fr-${safeId(key)}`} rows={2} value={fr} onChange={(e) => setFr(e.target.value)} className="field-input resize-y" />
                              </div>
                              <div>
                                <label className="field-label">EN</label>
                                <textarea data-testid={`site-text-en-${safeId(key)}`} rows={2} value={en} onChange={(e) => setEn(e.target.value)} className="field-input resize-y" />
                              </div>
                              <div className="flex gap-2">
                                <button
                                  data-testid={`site-text-save-${safeId(key)}`}
                                  onClick={() => saveText(key)}
                                  disabled={saving}
                                  className="btn-outline-elegant inline-flex items-center gap-2 !px-4 !py-2 !text-[11px] text-maquis-deep hover:bg-maquis-deep hover:text-casse disabled:opacity-50"
                                >
                                  <Check size={13} strokeWidth={2} /> {t.admin.site_save}
                                </button>
                                <button
                                  data-testid={`site-text-cancel-${safeId(key)}`}
                                  onClick={() => setEditing(null)}
                                  className="inline-flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-[0.12em] text-maquis-soft hover:text-maquis-deep"
                                >
                                  <X size={13} strokeWidth={2} /> {t.admin.site_cancel}
                                </button>
                              </div>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  {page.images.length > 0 && (
                    <>
                      <p className="field-label mt-8">{t.admin.site_images}</p>
                      <div className="mt-3 flex flex-wrap gap-4">
                        {page.images.map(({ key, label }) => (
                          <div key={key} data-testid={`site-image-${key}`} className="w-40">
                            <div className="relative h-24 overflow-hidden rounded-md border border-maquis-deep/10 bg-ivoire">
                              <img src={simg(key)} alt={label} className="h-full w-full object-cover" />
                              <label
                                data-testid={`site-image-upload-${key}`}
                                className="absolute bottom-1.5 right-1.5 cursor-pointer rounded-full bg-mer/60 p-2 text-casse transition-colors hover:bg-mer"
                                aria-label={t.admin.site_edit}
                              >
                                <Pencil size={12} strokeWidth={2} />
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(key, e)} />
                              </label>
                            </div>
                            <p className="mt-1.5 text-[11px] uppercase tracking-[0.1em] text-maquis-soft">
                              {uploading === key ? t.admin.uploading : label}
                            </p>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
